import React from 'react';
import { User } from '../types/User';
import { Info } from 'lucide-react';
import CustomNotification from './CustomNotification';
import { NetworkingService } from '../services/networkingService';

interface FavoritesModalProps {
  user: User;
  currentUser: User;
  onClose: () => void;
  onAdded?: () => void;
}

const FavoritesModal: React.FC<FavoritesModalProps> = ({ user, currentUser, onClose, onAdded }) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [notification, setNotification] = React.useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ show: false, message: '', type: 'info' });

  const handleAddToFavorites = async () => {
    if (submitting) return;
    setSubmitting(true);

    const result = await NetworkingService.sendConnectionRequest(currentUser.id, user.id);
    
    if (result.success) {
      setNotification({
        show: true,
        message: result.alreadyExists
          ? `${user.name} is already in your favorites`
          : `Successfully connected with ${user.name}!`,
        type: result.alreadyExists ? 'info' : 'success'
      });

      onAdded?.();
      
      setTimeout(() => {
        onClose();
      }, 1200);
    } else {
      setNotification({
        show: true,
        message: result.error || 'Failed to add connection',
        type: 'error'
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          {/* Icon and Question */}
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Info className="w-6 h-6" style={{ color: '#C89116' }} />
            </div>
          </div>
          
          <h2 className="text-lg font-medium text-gray-800 text-center mb-6">
            Add this attendee to your favorites?
          </h2>
          
          {/* User Info */}
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-4 mb-6 border border-yellow-200/50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center shadow-sm">
                <span className="text-sm font-semibold text-gray-700">
                  {user.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">{user.name}</p>
                <p className="text-sm text-gray-600">{user.position}</p>
                <p className="text-sm text-gray-500">{user.company}</p>
                <p className="text-sm text-gray-500">{user.country}</p>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:shadow-sm transition-all duration-300 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddToFavorites}
              disabled={submitting}
              className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 hover:shadow-sm transition-all duration-300 font-medium disabled:opacity-50"
              style={{ backgroundColor: '#C89116' }}
            >
              {submitting ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      </div>

      {/* Custom Notification */}
      {notification.show && (
        <CustomNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default FavoritesModal;
